import { lazy, Suspense, useEffect, useMemo, useState, type ReactElement } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import type { User } from 'firebase/auth'
import { ChatPage } from './features/chat/ChatPage'
import UpgradeModal from './features/billing/UpgradeModal'
import { TrialBanner } from './features/billing/TrialBanner'
import { OnboardingModal } from './features/billing/OnboardingModal'
import { RenewalBanner } from './features/billing/RenewalBanner'
import { AuthModal } from './features/auth/AuthModal'
import { translations, type Language } from './translations'
import { SearchPage } from './features/search/pages/SearchPage'
import { ProfilePage } from './features/profile/ProfilePage'
import { useLawAuth } from './features/auth/LawAuthProvider'
import type { AppTab } from './types/tabs'
import { MobileNav } from './components/MobileNav'
import { EServicesPage } from './features/resources/EServicesPage'

// Lazy load admin panel - only loaded when admin accesses /admin
const AdminDashboardPage = lazy(() => import('./features/admin/AdminPanel').then(m => ({ default: m.AdminDashboardPage })))
const UserDetailsPage = lazy(() => import('./features/admin/UserDetailsPage'))

const STORAGE_KEYS = {
    activeTab: 'jurimaroc.activeTab'
}

function App() {
    const [lang, setLang] = useState<Language>('fr')
    const t = translations[lang]
    const isRTL = lang === 'ar'

    const [activeTab, setActiveTab] = useState<AppTab>('search')
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)
    const [showOnboarding, setShowOnboarding] = useState(false)
    const [showAuthModal, setShowAuthModal] = useState(false)
    const navigate = useNavigate()

    const {
        user,
        isAdmin,
        subscriptionEnd,
        showProfileModal,
        setShowProfileModal,
        handleLogout,
        isUserMenuOpen,
        setIsUserMenuOpen,
        plan,
        isLoadingProfile,
        isAuthLoading,
        startTrial,
        hasSeenOnboarding: dbHasSeenOnboarding,
        markOnboardingSeen
    } = useLawAuth()

    // Wrapper to open auth modal (async for component prop types)
    const openAuthModal = async () => {
        setShowAuthModal(true)
    }

    const isMobile = useMemo(
        () => typeof window !== 'undefined' && window.matchMedia?.('(max-width: 640px)').matches,
        []
    )

    // Redirect to profile page if incomplete profile logic triggers
    useEffect(() => {
        if (showProfileModal) {
            setShowProfileModal(false)
            navigate('/profile')
        }
    }, [showProfileModal, navigate, setShowProfileModal])


    useEffect(() => {
        try {
            const storedTab = localStorage.getItem(STORAGE_KEYS.activeTab) as AppTab | null
            if (storedTab === 'search' || storedTab === 'chat') setActiveTab(storedTab)
        } catch {
            // ignore
        }
    }, [])

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEYS.activeTab, activeTab)
        } catch {
            // ignore persistence errors
        }
    }, [activeTab])

    // Show onboarding modal for new users
    useEffect(() => {
        if (!user || isLoadingProfile) return

        const isPremium = plan?.toLowerCase().includes('premium')
        const isTrial = plan?.toLowerCase() === 'trial'

        // If user is already premium or trial, don't show
        if (isPremium || isTrial) {
            return
        }

        // Only show if not seen before (from DB)
        // Add a small delay to prevent race conditions where plan loads moments after profile
        const timer = setTimeout(() => {
            if (!dbHasSeenOnboarding) {
                setShowOnboarding(true)
            }
        }, 1000)

        return () => clearTimeout(timer)
    }, [user, isLoadingProfile, plan, dbHasSeenOnboarding])

    const handleCloseOnboarding = async () => {
        setShowOnboarding(false)
        await markOnboardingSeen()
    }

    const openAdminDashboard = () => {
        setIsUserMenuOpen(false)
        navigate('/admin')
    }

    const hasLoadedPlanState = !user || !isLoadingProfile
    const normalizedPlan = hasLoadedPlanState ? (plan || 'free').toLowerCase() : ''
    const isPremiumPlan = hasLoadedPlanState && normalizedPlan.includes('premium')
    const isTrial = hasLoadedPlanState && normalizedPlan === 'trial'
    const planLabel = isPremiumPlan ? t.planPremiumLabel : (isTrial ? t.planTrialLabel : t.planFreeLabel)

    const homeScreen = (
        <div
            className={`min-h-dvh bg-linear-to-b from-slate-50 to-slate-100 font-sans text-slate-900 ${isRTL ? 'rtl' : ''}`}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            {/* Trial Banner - shows for trial/free users */}
            {hasLoadedPlanState && !isPremiumPlan && (
                <TrialBanner
                    user={user}
                    lang={lang}
                    onUpgradeClick={() => setShowUpgradeModal(true)}
                />
            )}

            <div className="flex min-h-dvh flex-col">

                {/* Subscription Renewal Banner */}
                <RenewalBanner
                    plan={plan}
                    subscriptionEnd={subscriptionEnd}
                    onRenewClick={() => setShowUpgradeModal(true)}
                />

                <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur">
                    <div className="flex items-center justify-between px-4 py-3 sm:px-6">
                        <div className="flex items-center gap-3">
                            <div className="flex flex-col items-start leading-tight">
                                <img src="/logo.svg" alt="JuriMaroc Logo" className="h-12 w-auto sm:h-14" />
                                <div className="text-xs font-semibold text-slate-700 sm:text-sm">{t.appTitle}</div>
                            </div>
                        </div>

                        <div className="hidden rounded-xl bg-slate-100 p-1 sm:flex">
                            <button
                                type="button"
                                onClick={() => setActiveTab('search')}
                                className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'search' ? 'bg-white text-blue-600 shadow' : 'text-slate-500 hover:text-slate-700'}`}
                                aria-label={t.searchTab}
                                title={t.searchTab}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                >
                                    <path d="M10 2a8 8 0 1 0 4.9 14.32l4.39 4.39 1.42-1.42-4.39-4.39A8 8 0 0 0 10 2zm0 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12z" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('chat')}
                                className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'chat' ? 'bg-white text-blue-600 shadow' : 'text-slate-500 hover:text-slate-700'}`}
                                aria-label={t.chatTab}
                                title={t.chatTab}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                >
                                    <path d="M4 4h16v12H7l-3 3V4zm2 2v9.17L6.17 15H18V6H6zm2 2h8v2H8V8zm0 4h6v2H8v-2z" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex gap-1">
                                <button
                                    type="button"
                                    onClick={() => setLang('fr')}
                                    className={`rounded-lg border px-2 py-1.5 text-xs font-medium ${lang === 'fr' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600'}`}
                                >
                                    FR
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setLang('ar')}
                                    className={`rounded-lg border px-2 py-1.5 text-xs font-medium ${lang === 'ar' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600'} font-arabic`}
                                >
                                    عربي
                                </button>
                            </div>

                            {user && (
                                <div className="flex items-center gap-2 text-xs">
                                    {hasLoadedPlanState ? (
                                        <>
                                            <span className={`rounded-full border px-3 py-1 font-semibold ${isPremiumPlan ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                                                {planLabel}
                                            </span>
                                            {!isPremiumPlan && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowUpgradeModal(true)}
                                                    className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                                                >
                                                    {t.planUpgradeCta}
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        <div className="flex h-7 items-center px-2">
                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="mx-1 h-6 w-px bg-slate-200"></div>

                            {isAuthLoading ? (
                                <div className="flex h-8 w-8 items-center justify-center">
                                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
                                </div>
                            ) : user ? (
                                <div className="relative">
                                    <button
                                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                        className="flex h-8 w-8 items-center justify-center rounded-full border border-blue-200 bg-blue-100 text-sm font-bold text-blue-600 transition-colors hover:bg-blue-200"
                                        title={user.email || 'User'}
                                    >
                                        {user.photoURL ? (
                                            <img src={user.photoURL} alt="User" className="h-full w-full rounded-full object-cover" />
                                        ) : (
                                            user.email ? user.email[0].toUpperCase() : 'U'
                                        )}
                                    </button>

                                    {isUserMenuOpen && (
                                        <div className="absolute right-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                                            <div className="py-1">
                                                <button
                                                    onClick={() => {
                                                        setIsUserMenuOpen(false)
                                                        navigate('/profile')
                                                    }}
                                                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                                                    {t.menuProfile}
                                                </button>
                                                {isAdmin && (
                                                    <button
                                                        onClick={openAdminDashboard}
                                                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                                                    >
                                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                                                        Admin Dashboard
                                                    </button>
                                                )}
                                                <button
                                                    onClick={handleLogout}
                                                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
                                                    {t.menuLogout}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button
                                    onClick={() => setShowAuthModal(true)}
                                    className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
                                >
                                    {lang === 'ar' ? 'دخول' : 'Login'}
                                </button>
                            )}
                        </div>
                    </div>
                </header>

                <main className={`flex-1 min-h-0 flex-col overflow-hidden ${isMobile ? 'pb-28' : 'pb-0'} flex`}>
                    <section className={`${activeTab === 'search' ? 'flex flex-col' : 'hidden'} h-full min-h-0 flex-1 w-full max-w-5xl mx-auto px-4 pt-4 sm:px-6 sm:py-6`}>
                        <SearchPage
                            lang={lang}
                            t={t}
                            user={user}
                            onRequireLogin={openAuthModal}
                            onShowUpgrade={() => setShowUpgradeModal(true)}
                        />
                    </section>

                    <section className={`${activeTab === 'chat' ? 'flex flex-col' : 'hidden'} h-full min-h-0 flex-1`}>
                        <ChatPage
                            lang={lang}
                            t={t}
                            user={user}
                            onLogin={openAuthModal}
                            onShowUpgrade={() => setShowUpgradeModal(true)}
                        />
                    </section>
                </main>

                {isMobile && <MobileNav activeTab={activeTab} onTabChange={setActiveTab} t={t} />}
            </div>

            <UpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                user={user}
                lang={lang}
            />

            {/* <ProfileModal ... /> removed in favor of /profile page */}

            {/* Auth modal for login/signup */}
            <AuthModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                lang={lang}
            />

            {/* Onboarding modal for new users */}
            {showOnboarding && plan === 'free' && (
                <OnboardingModal
                    lang={lang}
                    onClose={handleCloseOnboarding}
                    onStartTrial={startTrial}
                />
            )}
        </div>
    )

    // (Removed inner lazy definitions)

    return (
        <Routes>
            <Route path="/" element={homeScreen} />
            <Route path="/profile" element={<ProfilePage lang={lang} />} />
            <Route
                path="/admin"
                element={
                    <RequireAdminRoute user={user} isAdmin={isAdmin} isLoading={isAuthLoading}>
                        <Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div></div>}>
                            <AdminDashboardPage
                                onLogout={async () => {
                                    await handleLogout()
                                    navigate('/')
                                }}
                            />
                        </Suspense>
                    </RequireAdminRoute>
                }
            />
            <Route path="/admin/users/:userId"
                element={
                    <RequireAdminRoute user={user} isAdmin={isAdmin} isLoading={isAuthLoading}>
                        <Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div></div>}>
                            <UserDetailsPage />
                        </Suspense>
                    </RequireAdminRoute>
                }
            />
            <Route path="/e-services" element={
                <div className={`min-h-dvh bg-slate-50 font-sans ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
                    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur">
                        <div className="flex items-center justify-between px-4 py-3 sm:px-6">
                            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                                <img src="/logo.svg" alt="JuriMaroc Logo" className="h-10 w-auto" />
                                <div className="text-xs font-semibold text-slate-700 sm:text-sm">{t.appTitle}</div>
                            </div>
                            <div className="flex items-center gap-3">
                                {/* Language Toggles */}
                                <div className="flex gap-1">
                                    <button onClick={() => setLang('fr')} className={`rounded-lg border px-2 py-1.5 text-xs font-medium ${lang === 'fr' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600'}`}>FR</button>
                                    <button onClick={() => setLang('ar')} className={`rounded-lg border px-2 py-1.5 text-xs font-medium ${lang === 'ar' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600'} font-arabic`}>عربي</button>
                                </div>
                                {/* Close Button */}
                                <button onClick={() => navigate('/')} className="rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200">
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                        </div>
                    </header>
                    <EServicesPage lang={lang} />
                </div>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    )
}

export default App

function RequireAdminRoute({ user, isAdmin, isLoading, children }: { user: User | null; isAdmin: boolean; isLoading: boolean; children: ReactElement }) {
    if (isLoading) {
        return <div className="flex h-screen items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div></div>
    }
    if (!user) {
        return <Navigate to="/" replace />
    }
    if (!isAdmin) {
        return <Navigate to="/" replace />
    }
    return children
}
